import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBusiness } from "@/hooks/use-business";
import type { Tables } from "@/integrations/supabase/types";
import { useToast } from "@/hooks/use-toast";

export type SubscriptionPlan = Tables<"subscription_plans">;

export function useSubscription() {
  const { business, refetch: refetchBusiness } = useBusiness();
  const { toast } = useToast();

  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUpdatingPlan, setIsUpdatingPlan] = useState(false);

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: err } = await supabase
        .from("subscription_plans")
        .select("*")
        .order("price_ghs", { ascending: true });
      if (err) throw err;
      setPlans((data || []) as SubscriptionPlan[]);
    } catch (e: any) {
      setError(e?.message || "Failed to load plans");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const currentPlan = useMemo(() => {
    if (!business) return null;
    const planId = (business as any).plan_id || "starter";
    return plans.find((p) => p.id === planId) || null;
  }, [business, plans]);

  const switchPlan = useCallback(
    async (planId: string) => {
      if (!business) {
        toast({ title: "No business found", variant: "destructive" });
        return;
      }

      setIsUpdatingPlan(true);
      try {
        const { error: err } = await supabase
          .from("businesses")
          .update({ plan_id: planId, subscription_status: "active" } as any)
          .eq("id", business.id);
        if (err) throw err;
        await refetchBusiness();
        await fetchPlans();
        toast({ title: "Plan updated", description: "Your subscription plan has been updated." });
      } catch (e: any) {
        toast({
          title: "Failed to update plan",
          description: e?.message || "Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsUpdatingPlan(false);
      }
    },
    [business, fetchPlans, refetchBusiness, toast]
  );

  return { plans, currentPlan, loading, error, switchPlan, isUpdatingPlan, refetch: fetchPlans };
}


